// Tech Support Co-Pilot — the in-page email panel (left side of the page).
//
// Reads John's bound Tech Support inbox LIVE through the co-pilot mail proxy
// (/api/tech-support-copilot/mail/*), which reads Gmail directly — so the agent's
// proposed DRAFTS show up here too. A real little mail client: folder views
// (Inbox / Drafts / Sent / Trash), Gmail-powered search, load-more pagination,
// reply / reply-all (quoted) / forward (files ride along), archive + trash, and
// a one-click human Send for the agent's proposed draft (the agent itself never
// sends). The agent reads/drafts via its own gmail MCP tools; this panel is what
// John looks at + acts on.

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import LinkifiedText from '../common/LinkifiedText';
import { useGuruWidgetViewContext } from '../../hooks/useGuruWidgetViewContext';
import AttachmentList from '../portal/AttachmentList';
import EmailChainThread from '../portal/EmailChainThread';
import EmailHtmlViewer from '../portal/EmailHtmlViewer';
import type { AttachmentDownloader, ChainDetail } from '../../types/portal';
import {
  archiveMailThread,
  discardMailDraft,
  downloadMailAttachment,
  getMailThread,
  listMailDrafts,
  listMailThreads,
  sendMailDraft,
  setMailThreadRead,
  trashMailThread,
  type MailDraftSummary,
  type MailFolder,
  type MailThreadSummary,
  type SendDraftResult,
} from '../../services/techSupportCopilot';
import { trackEvent } from '../../services/portalTracking';
import { senderAddressSuffix } from '../../utils/senderDisplay';
import TechSupportComposer from './TechSupportComposer';
import type { ComposerInit } from './composerTypes';
import {
  buildForwardQuoteHtml,
  buildReplyQuoteHtml,
  forwardSubject,
  replyAllRecipients,
  replyRecipient,
} from './quoteBuilder';
import styles from './TechSupportMailPanel.module.css';

/** Short, locale-aware date label for a row (Gmail Date header is RFC 2822). */
function shortDate(raw: string | null): string {
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function httpStatus(err: unknown): number | undefined {
  return (err as { response?: { status?: number } })?.response?.status;
}

const GMAIL_DRAFTS_URL = 'https://mail.google.com/mail/u/0/#drafts';

/** The panel's folder tabs. Drafts is draft-backed; the rest are thread-backed. */
type PanelFolder = MailFolder | 'drafts';
const FOLDERS: { key: PanelFolder; label: string }[] = [
  { key: 'inbox', label: 'Inbox' },
  { key: 'drafts', label: 'Drafts' },
  { key: 'sent', label: 'Sent' },
  { key: 'trash', label: 'Trash' },
];

/** Human-readable outcome of a send: delivered, sandbox-redirected, or suppressed. */
function sendOutcomeNote(r: SendDraftResult): string {
  if (!r.sandbox) return 'Sent ✓ — your message is on its way.';
  if (r.sent) {
    const target = r.redirect_email ? ` (${r.redirect_email})` : '';
    return `Sent to the sandbox test address${target} — real recipients were not emailed.`;
  }
  return 'Saved — not delivered on staging (sandbox).';
}

interface TechSupportMailPanelProps {
  /** Selecting a thread binds/resumes its co-pilot conversation + gives context. */
  onThreadSelected?: (threadId: string, subject: string) => void;
  /** Returning to the inbox clears the per-turn thread context. */
  onThreadCleared?: () => void;
  /** Bumped by the page when an agent turn finishes, so a freshly proposed draft
   *  (and any inbox change) shows up here without a manual refresh. */
  reloadSignal?: number;
  /** John's configured signature — previewed under each draft (it's appended
   *  automatically on send), so what he sees in the card is what goes out. */
  signatureHtml?: string;
  /** Opens the page's signature editor from the draft card / composer preview
   *  (kanban 631e1721: John could not find the editor in the Merlin header). */
  onEditSignature?: () => void;
}

export default function TechSupportMailPanel({
  onThreadSelected,
  onThreadCleared,
  reloadSignal,
  signatureHtml,
  onEditSignature,
}: TechSupportMailPanelProps = {}) {
  const [folder, setFolder] = useState<PanelFolder>('inbox');
  const [threads, setThreads] = useState<MailThreadSummary[]>([]);
  const [drafts, setDrafts] = useState<MailDraftSummary[]>([]);
  const [accountEmail, setAccountEmail] = useState('');
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [notConnected, setNotConnected] = useState(false);
  // Gmail search within the active folder: the input is uncontrolled noise until
  // John submits; `activeSearch` is what the list is actually filtered by.
  const [searchInput, setSearchInput] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [nextPageToken, setNextPageToken] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  // Unread conversations across the whole inbox for this user (server-counted;
  // null until an inbox load reports it). Drives the Inbox tab badge. The
  // read/unread marker is the portal's own per-user state (John's 2026-10-14
  // ask) — opening a conversation marks it read, a newer customer message or
  // "Mark as unread" makes it unread again.
  const [unreadCount, setUnreadCount] = useState<number | null>(null);
  // Monotonic list-request id: a slow response for a folder/search the user has
  // since moved away from must NOT clobber the current list. Each loadThreads
  // bumps it and only applies its result if still the latest.
  const loadSeqRef = useRef(0);
  // Latest list for callbacks that must not re-create on every row change.
  const threadsRef = useRef<MailThreadSummary[]>([]);
  threadsRef.current = threads;

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedSubject, setSelectedSubject] = useState('');
  // ONE scroll container (.body) hosts the list AND an open conversation, so
  // reading a long email used to dump ← Back deep into the list's oldest rows
  // (John Kirk's 2026-08-13 report). Remember where John was in the list, open
  // every thread/composer at the top, and restore the list position on Back.
  const bodyRef = useRef<HTMLDivElement>(null);
  const listScrollTopRef = useRef(0);
  const [thread, setThread] = useState<ChainDetail | null>(null);
  const [loadingThread, setLoadingThread] = useState(false);
  const [threadError, setThreadError] = useState<string | null>(null);

  const [sending, setSending] = useState<string | null>(null);
  const [sendNote, setSendNote] = useState<string | null>(null);
  // Archive / Trash / Discard in flight (button-disable + double-click guard).
  const [acting, setActing] = useState<string | null>(null);

  // The inline composer (new email / reply / forward / edit a draft). When set,
  // it takes over the panel body. John can work the inbox WITHOUT the agent.
  const [composer, setComposer] = useState<ComposerInit | null>(null);
  // The conversation John is answering, kept visible (scrollable) BELOW the open
  // composer — editing a draft or writing a reply must never hide the original
  // email. Reused from the open thread when there is one; fetched by the draft's
  // thread id when editing straight from the Drafts folder.
  const [composerThread, setComposerThread] = useState<ChainDetail | null>(null);
  const [composerThreadLoading, setComposerThreadLoading] = useState(false);
  // 📋 Templates stays in the panel's FIXED header while the composer is open
  // (John, 2026-10-06: "stay near the top even when we start a reply"), the same
  // spot the thread view shows it. Each press bumps this counter; the composer
  // opens its picker on every new value. Mirrors the composer's save/send state
  // so the header button greys out in step with its own buttons.
  const [templatesSignal, setTemplatesSignal] = useState(0);
  const [composerBusy, setComposerBusy] = useState(false);
  // Monotonic guard: a slow context fetch for a composer John has since closed
  // (or replaced) must not attach the wrong conversation to the current one.
  const composerCtxSeqRef = useRef(0);

  // Tell the Guru feedback widget what this panel is showing (folder, open
  // thread, composer) — none of it reaches the URL, so widget reports from
  // the tech-support co-pilot would otherwise carry no email identifiers.
  useGuruWidgetViewContext('tech_support_mail', {
    mail_folder: folder,
    mail_account: accountEmail || undefined,
    thread_id: selectedId ?? undefined,
    thread_subject: selectedId ? selectedSubject || undefined : undefined,
    composer_mode: composer?.mode,
    email_id: composer?.draftId ?? undefined,
    email_subject: composer?.subject,
  });

  /** Set (or clear) the conversation shown under the composer, invalidating any
   *  in-flight context fetch. */
  const setComposerContext = useCallback((t: ChainDetail | null) => {
    composerCtxSeqRef.current += 1;
    setComposerThread(t);
    setComposerThreadLoading(false);
  }, []);

  /** Fetch the composer's context conversation by thread id (Drafts folder —
   *  no thread is open there). */
  const fetchComposerContext = useCallback((threadId: string) => {
    const seq = ++composerCtxSeqRef.current;
    setComposerThread(null);
    setComposerThreadLoading(true);
    getMailThread(threadId)
      .then((t) => {
        if (seq === composerCtxSeqRef.current) setComposerThread(t);
      })
      .catch(() => {
        /* context is best-effort — the composer still works without it */
      })
      .finally(() => {
        if (seq === composerCtxSeqRef.current) setComposerThreadLoading(false);
      });
  }, []);

  // Inbound attachments are fetched live from Gmail by (message_id,
  // attachment_id) — ChainEmail.id IS the Gmail message id here.
  const coPilotDownload = useCallback<AttachmentDownloader>(
    (att, emailId) => downloadMailAttachment(emailId, att.attachment_id ?? '', att.filename),
    [],
  );

  /** Save the list's scroll offset — only while the list is what's on screen. */
  const captureListScroll = useCallback(() => {
    if (!selectedId && !composer && bodyRef.current) {
      listScrollTopRef.current = bodyRef.current.scrollTop;
    }
  }, [selectedId, composer]);

  // Runs on every list ⇄ thread/composer view flip, before paint: a conversation
  // or the composer always opens at the top; returning to the list restores the
  // saved offset (0 after a folder switch or a new search).
  useLayoutEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    el.scrollTop = selectedId || composer ? 0 : listScrollTopRef.current;
  }, [selectedId, composer]);

  const draftFor = useCallback(
    (threadId: string | null): MailDraftSummary | undefined =>
      threadId ? drafts.find((d) => d.thread_id === threadId) : undefined,
    [drafts],
  );

  const loadThreads = useCallback(async () => {
    const seq = ++loadSeqRef.current;
    const isStale = () => seq !== loadSeqRef.current;
    setLoadingList(true);
    setListError(null);
    setNotConnected(false);
    try {
      // The Drafts folder shows only drafts — don't fetch a thread list it
      // never renders (and never let that list overwrite another folder's).
      const isDrafts = folder === 'drafts';
      const [threadData, draftData] = await Promise.all([
        isDrafts
          ? Promise.resolve(null)
          : listMailThreads({ limit: 20, folder, search: activeSearch || undefined }),
        // A FAILED drafts fetch (transient Gmail 429, etc.) must NOT clear the
        // list — that was blanking every "draft ready" pill on refresh even
        // though the drafts still existed. Sentinel null = "keep what we have".
        listMailDrafts().catch(() => null),
      ]);
      if (isStale()) return; // a newer folder/search request superseded this one
      if (threadData) {
        setThreads(threadData.threads);
        setAccountEmail(threadData.account_email);
        setNextPageToken(threadData.next_page_token ?? null);
        if (folder === 'inbox' && !activeSearch) {
          setUnreadCount(threadData.unread_count ?? null);
        }
      } else {
        setThreads([]);
        setNextPageToken(null);
      }
      if (draftData) setDrafts(draftData.drafts ?? []);
    } catch (err) {
      if (isStale()) return;
      const status = httpStatus(err);
      if (status === 404) setNotConnected(true);
      // 503 = Gmail momentarily throttled/unreachable (transient). Say "try again"
      // rather than the generic failure — and never a blank/"no conversations".
      else if (status === 503)
        setListError('The inbox is temporarily unavailable (Gmail is busy). Try again in a moment.');
      else setListError('Could not load the inbox.');
    } finally {
      if (!isStale()) setLoadingList(false);
    }
  }, [folder, activeSearch]);

  useEffect(() => {
    void loadThreads();
  }, [loadThreads]);

  // When the page signals a finished agent turn, re-pull threads + drafts so the
  // agent's just-proposed draft appears with no manual refresh. The 0 initial
  // value is skipped (the mount effect already did the first load).
  useEffect(() => {
    if (!reloadSignal) return;
    void loadThreads();
  }, [reloadSignal, loadThreads]);

  const loadMore = useCallback(async () => {
    if (!nextPageToken || loadingMore || folder === 'drafts') return;
    const seq = loadSeqRef.current; // page belongs to the current list request
    setLoadingMore(true);
    try {
      const page = await listMailThreads({
        limit: 20,
        folder,
        search: activeSearch || undefined,
        pageToken: nextPageToken,
      });
      // A folder switch / search / refresh since we started → drop these rows.
      if (seq !== loadSeqRef.current) return;
      setThreads((prev) => {
        const seen = new Set(prev.map((t) => t.thread_id));
        return [...prev, ...page.threads.filter((t) => !seen.has(t.thread_id))];
      });
      setNextPageToken(page.next_page_token ?? null);
    } catch {
      if (seq === loadSeqRef.current) setListError('Could not load more conversations.');
    } finally {
      if (seq === loadSeqRef.current) setLoadingMore(false);
    }
  }, [nextPageToken, loadingMore, folder, activeSearch]);

  const switchFolder = useCallback(
    (f: PanelFolder) => {
      // A different folder is a fresh list — start it at the top (the layout
      // effect only fires on view flips, so also reset directly for the
      // list → list case).
      listScrollTopRef.current = 0;
      if (bodyRef.current) bodyRef.current.scrollTop = 0;
      setFolder(f);
      setSelectedId(null);
      setThread(null);
      setThreadError(null);
      setSendNote(null);
      setComposer(null);
      setComposerContext(null);
      setSearchInput('');
      setActiveSearch('');
      setNextPageToken(null);
      onThreadCleared?.();
    },
    [onThreadCleared, setComposerContext],
  );

  const submitSearch = useCallback(() => {
    // New results, new list — show them from the top.
    listScrollTopRef.current = 0;
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
    setActiveSearch(searchInput.trim());
  }, [searchInput]);

  // Flip one row's read marker locally (the server state already changed) and
  // keep the Inbox badge in step; records the transition as a portal event.
  const setRowUnread = useCallback(
    (threadId: string, unread: boolean, source: 'open' | 'reply' | 'mark_unread') => {
      const row = threadsRef.current.find((t) => t.thread_id === threadId);
      if (!row || row.unread === unread) return; // not listed here, or no transition
      setThreads((prev) =>
        prev.map((t) => (t.thread_id === threadId ? { ...t, unread } : t)),
      );
      setUnreadCount((n) => (n == null ? n : Math.max(0, n + (unread ? 1 : -1))));
      trackEvent('action', unread ? 'tsc_thread_mark_unread' : 'tsc_thread_mark_read', {
        thread_id: threadId,
        source,
        // The Mark unread button is annotated data-action="tsc_thread_mark_unread"
        // and this fires after an awaited request, past the bridge's dedupe
        // window: keep the portal_events row, skip the recorder forward.
        ...(source === 'mark_unread' ? { recorded_by_dom: true } : {}),
      });
    },
    [],
  );

  const openThread = useCallback(
    async (row: MailThreadSummary) => {
      captureListScroll();
      trackEvent('click', 'tsc_thread_open', {
        thread_id: row.thread_id,
        folder,
        was_unread: row.unread,
      });
      setSelectedId(row.thread_id);
      setSelectedSubject(row.subject ?? '(no subject)');
      onThreadSelected?.(row.thread_id, row.subject ?? '(no subject)');
      setThread(null);
      setThreadError(null);
      setSendNote(null);
      setLoadingThread(true);
      try {
        setThread(await getMailThread(row.thread_id));
        // Opening marks the thread read server-side (per-user read state +
        // Gmail UNREAD cleared) — reflect that on the row immediately.
        setRowUnread(row.thread_id, false, 'open');
      } catch {
        setThreadError('Could not open this conversation.');
      } finally {
        setLoadingThread(false);
      }
    },
    [onThreadSelected, captureListScroll, folder, setRowUnread],
  );


  const back = useCallback(() => {
    setSelectedId(null);
    setThread(null);
    setThreadError(null);
    setSendNote(null);
    onThreadCleared?.();
  }, [onThreadCleared]);

  // "Mark as unread" on an open conversation: per-user portal state, so the
  // row shows the dot again on the next visit (and after reload).
  const onMarkUnread = useCallback(async () => {
    if (!selectedId || acting) return;
    setActing('unread');
    try {
      await setMailThreadRead(selectedId, false);
      setRowUnread(selectedId, true, 'mark_unread');
      back();
    } catch {
      setSendNote('Could not mark this conversation as unread.');
    } finally {
      setActing(null);
    }
  }, [selectedId, acting, setRowUnread, back]);

  const onSend = useCallback(
    async (draftId: string) => {
      setSending(draftId);
      setSendNote(null);
      try {
        const r = await sendMailDraft(draftId);
        setSendNote(sendOutcomeNote(r));
        const repliedTo = r.thread_id ?? selectedId;
        if (r.sent && repliedTo) setRowUnread(repliedTo, false, 'reply');
        await loadThreads();
        if (r.sent && selectedId) {
          try {
            setThread(await getMailThread(selectedId));
          } catch {
            /* thread refresh is best-effort */
          }
        }
      } catch {
        setSendNote('Could not send the draft.');
      } finally {
        setSending(null);
      }
    },
    [loadThreads, selectedId, setRowUnread],
  );

  // --- Archive / Trash (thread) + Discard (draft) ---------------------------

  const onArchive = useCallback(async () => {
    if (!selectedId || acting) return;
    setActing('archive');
    try {
      await archiveMailThread(selectedId);
      setSendNote('Archived — out of the inbox, still searchable.');
      back();
      await loadThreads();
      setSendNote('Archived — out of the inbox, still searchable.');
    } catch {
      setSendNote('Could not archive this conversation.');
    } finally {
      setActing(null);
    }
  }, [selectedId, acting, back, loadThreads]);

  const onTrash = useCallback(async () => {
    if (!selectedId || acting) return;
    if (!window.confirm('Move this conversation to Trash?')) return;
    setActing('trash');
    try {
      await trashMailThread(selectedId);
      back();
      await loadThreads();
      setSendNote('Moved to Trash (recoverable there for ~30 days).');
    } catch {
      setSendNote('Could not move this conversation to Trash.');
    } finally {
      setActing(null);
    }
  }, [selectedId, acting, back, loadThreads]);

  const onDiscardDraft = useCallback(
    async (draft: MailDraftSummary) => {
      if (acting) return;
      if (!window.confirm('Discard this draft? This cannot be undone.')) return;
      setActing(draft.draft_id);
      try {
        await discardMailDraft(draft.draft_id);
        setSendNote('Draft discarded.');
        await loadThreads();
      } catch {
        setSendNote('Could not discard the draft.');
      } finally {
        setActing(null);
      }
    },
    [acting, loadThreads],
  );

  // --- Composer (new email / reply / reply-all / forward / edit a draft) ----

  const openCompose = useCallback(() => {
    captureListScroll();
    setSendNote(null);
    setComposer({ mode: 'new' });
    setComposerContext(null); // a brand-new email answers nothing
  }, [captureListScroll, setComposerContext]);

  // The customer on the open thread (latest message that isn't our own sent
  // reply) — the reply recipient, and the {name} a template auto-fills with.
  const threadCustomer = useCallback(() => {
    if (!thread) return undefined;
    const ordered = thread.emails;
    return [...ordered].reverse().find((e) => !e.is_sent_reply);
  }, [thread]);

  const openReply = useCallback(
    (openTemplates = false, replyAll = false) => {
      if (!thread) return;
      const ordered = thread.emails;
      const customer = threadCustomer();
      const head = ordered.find((e) => e.is_head) ?? ordered[ordered.length - 1];
      const subjRaw = head?.subject ?? selectedSubject ?? '';
      const subject = /^re:/i.test(subjRaw) ? subjRaw : `Re: ${subjRaw}`;
      const quoted = customer ? buildReplyQuoteHtml(customer) : '';
      // Who this reply goes to is decided SERVER-side (ChainDetail.reply_recipient)
      // — the sender is the wrong answer on the two shapes this mailbox is full
      // of: contact-form leads arrive from noreply@, and an already-answered
      // thread's newest message is our own. `null` means the server found no
      // replyable address, so open with an empty To and make John supply one
      // rather than quietly mailing the wrong party. `undefined` = a backend
      // without the field, so keep the historical sender-based prefill.
      const resolvedTo = replyRecipient(
        thread,
        customer?.from_address ?? head?.from_address,
      );
      const all =
        replyAll && customer
          ? replyAllRecipients(customer, accountEmail, resolvedTo)
          : null;
      setSendNote(null);
      setComposer({
        mode: 'reply',
        to: (all ? all.to : resolvedTo) || undefined,
        cc: all && all.cc.length ? all.cc.join(', ') : undefined,
        subject,
        // John types above the quoted original, like any mail client.
        bodyHtml: quoted ? `<p></p>${quoted}` : undefined,
        threadId: selectedId ?? undefined,
        openTemplates,
        recipientName: customer?.from_name || undefined,
      });
      setComposerContext(thread); // keep the original visible while John writes
    },
    [thread, threadCustomer, selectedId, selectedSubject, accountEmail, setComposerContext],
  );

  const openForward = useCallback(() => {
    if (!thread) return;
    const ordered = thread.emails;
    const head = ordered.find((e) => e.is_head) ?? ordered[ordered.length - 1];
    if (!head) return;
    setSendNote(null);
    setComposer({
      mode: 'forward',
      subject: forwardSubject(head.subject ?? selectedSubject),
      bodyHtml: `<p></p>${buildForwardQuoteHtml(head)}`,
      // The server re-attaches this message's files on the first save.
      forwardFromMessageId: head.id,
      recipientName: undefined,
    });
    setComposerContext(thread); // the message being forwarded stays in view
  }, [thread, selectedSubject, setComposerContext]);

  const openEditDraft = useCallback(
    (draft: MailDraftSummary, openTemplates = false) => {
      captureListScroll(); // no-op unless coming from a list (the Drafts folder)
      setSendNote(null);
      setComposer({
        mode: 'edit',
        to: draft.to ?? undefined,
        // Rehydrate Cc/Bcc: the composer's save-before-send REPLACES the whole
        // Gmail draft, so recipients it doesn't carry are silently stripped
        // from the delivered email (John Kirk's 2026-08-10 report).
        cc: draft.cc ?? undefined,
        bcc: draft.bcc ?? undefined,
        subject: draft.subject ?? undefined,
        body: draft.body_text ?? '',
        bodyHtml: draft.body_html ?? undefined,
        threadId: draft.thread_id ?? undefined,
        draftId: draft.draft_id,
        // Existing files are preserved server-side on edit — show them read-only.
        keptAttachments: (draft.attachments ?? []).map((a) => ({
          filename: a.filename,
          size: a.size,
        })),
        openTemplates,
        recipientName: threadCustomer()?.from_name || undefined,
      });
      // Keep the email being answered on screen. From the thread view the
      // conversation is already loaded; from the Drafts folder fetch it by the
      // draft's thread id.
      if (thread && selectedId && selectedId === draft.thread_id) {
        setComposerContext(thread);
      } else if (draft.thread_id) {
        fetchComposerContext(draft.thread_id);
      } else {
        setComposerContext(null);
      }
    },
    [threadCustomer, thread, selectedId, setComposerContext, fetchComposerContext, captureListScroll],
  );

  // The thread-header 📋 Templates button: one click lands John in the composer
  // with the picker already open — editing the thread's existing draft if there
  // is one, otherwise starting a reply — so the chosen template is inserted at
  // his cursor instead of blindly appended to the end of the draft.
  const openTemplatesComposer = useCallback(() => {
    const draft = draftFor(selectedId);
    trackEvent('click', 'tsc_templates_open', {
      source: 'thread_head',
      thread_id: selectedId,
      composer_mode: draft ? 'edit' : 'reply',
    });
    if (draft) openEditDraft(draft, true);
    else openReply(true);
  }, [draftFor, selectedId, openEditDraft, openReply]);

  // The header 📋 Templates button while the composer is open: pokes the
  // already-mounted composer to show its picker (insert lands at John's cursor).
  const openTemplatesInComposer = useCallback(() => {
    trackEvent('click', 'tsc_templates_open', {
      source: 'composer_header',
      thread_id: composer?.threadId ?? null,
      composer_mode: composer?.mode ?? null,
    });
    setTemplatesSignal((n) => n + 1);
  }, [composer?.threadId, composer?.mode]);

  const closeComposer = useCallback(() => {
    setComposer(null);
    setComposerContext(null);
  }, [setComposerContext]);

  const onComposerSaved = useCallback(() => {
    void loadThreads();
  }, [loadThreads]);

  const onComposerSent = useCallback(
    (result: SendDraftResult) => {
      setComposer(null);
      setComposerContext(null);
      setSendNote(sendOutcomeNote(result));
      const repliedTo = result.thread_id ?? composer?.threadId ?? selectedId;
      if (result.sent && repliedTo) setRowUnread(repliedTo, false, 'reply');
      void loadThreads();
      if (selectedId) {
        getMailThread(selectedId).then(setThread).catch(() => undefined);
      }
    },
    [loadThreads, selectedId, setComposerContext, composer?.threadId, setRowUnread],
  );

  const renderDraftCard = (draft: MailDraftSummary) => (
    <div
      className={styles.draftCard}
      data-target-draft-id={draft.draft_id}
      data-target-thread-id={draft.thread_id ?? undefined}
    >
      <div className={styles.draftHead}>
        <span className={styles.draftLabel}>✎ Proposed reply (draft)</span>
      </div>
      <div className={styles.draftSubject}>{draft.subject || '(no subject)'}</div>
      {draft.body_html ? (
        <div className={styles.draftBody}>
          <EmailHtmlViewer html={draft.body_html} />
        </div>
      ) : draft.body_text ? (
        // Full proposed body with its original line breaks/paragraphs preserved
        // (white-space: pre-wrap) — not the truncated Gmail snippet. URLs are
        // rendered as real links so John can test them before sending.
        <div className={styles.draftBody}>
          <LinkifiedText text={draft.body_text} />
        </div>
      ) : draft.snippet ? (
        <div className={styles.draftSnippet}>{draft.snippet}</div>
      ) : null}
      {draft.attachments && draft.attachments.length > 0 && (
        <AttachmentList
          attachments={draft.attachments}
          emailId={draft.message_id ?? ''}
          download={coPilotDownload}
        />
      )}
      {signatureHtml && (
        <div className={styles.draftSignature}>
          <div className={styles.draftSignatureLabel}>
            <span>Your signature (added automatically on send)</span>
            {onEditSignature && (
              <button
                type="button"
                className={styles.draftSignatureEdit}
                onClick={() => {
                  trackEvent('click', 'tsc_signature_edit', {
                    source: 'draft_card',
                    thread_id: draft.thread_id ?? null,
                    draft_id: draft.draft_id,
                  });
                  onEditSignature();
                }}
                title="Change the sign-off added to your replies (name, title, phone…)"
                data-action="tsc_signature_edit"
                data-outcome="open"
                data-record-value="source"
                value="draft_card"
              >
                ✎ Edit signature
              </button>
            )}
          </div>
          {/* John's own signature HTML — same dangerouslySetInnerHTML preview the
              orders-portal compose modal uses. */}
          <div dangerouslySetInnerHTML={{ __html: signatureHtml }} />
        </div>
      )}
      {draft.thread_id && (
        <div className={styles.draftQuoteNote}>
          The customer&apos;s original message is quoted below your reply
          automatically when it sends.
        </div>
      )}
      <div className={styles.draftActions}>
        <button
          type="button"
          className={styles.sendBtn}
          disabled={sending === draft.draft_id}
          onClick={() => void onSend(draft.draft_id)}
          data-action="tsc_mail_send"
          data-outcome="request"
          data-record-value="source"
          value="draft_card"
        >
          {sending === draft.draft_id ? 'Sending…' : 'Send'}
        </button>
        <button
          type="button"
          className={styles.editBtn}
          onClick={() => openEditDraft(draft)}
          data-action="tsc_draft_edit_open"
          data-outcome="open"
          data-record-value="source"
          value="draft_card"
        >
          ✎ Edit &amp; send
        </button>
        <a
          className={styles.openGmail}
          href={GMAIL_DRAFTS_URL}
          target="_blank"
          rel="noreferrer"
          data-action="tsc_draft_open_gmail"
        >
          Open in Gmail
        </a>
        {/* Delete-in-context: reviewing an unwanted proposed reply is exactly
            where John wants to throw it away, so the Drafts-folder Discard is
            mirrored here (far-right destructive slot) rather than making him
            hunt for the draft in the Drafts tab. Same handler + confirm. */}
        <button
          type="button"
          className={styles.rowDangerBtn}
          style={{ marginLeft: 'auto' }}
          disabled={acting === draft.draft_id}
          onClick={() => void onDiscardDraft(draft)}
          data-testid="draft-card-discard"
          title="Delete this draft — it won't be sent"
          data-action="tsc_draft_discard"
          data-outcome="request"
          data-record-value="source"
          value="draft_card"
        >
          🗑 Discard
        </button>
      </div>
    </div>
  );

  /** A row in the Drafts folder — John's saved drafts + the agent's proposals. */
  const renderDraftRow = (draft: MailDraftSummary) => (
    <li key={draft.draft_id} data-target-draft-id={draft.draft_id} data-target-thread-id={draft.thread_id ?? undefined}>
      <div className={styles.row}>
        <button
          type="button"
          className={styles.draftRowMain}
          onClick={() => openEditDraft(draft)}
          title="Open this draft in the editor"
          data-action="tsc_draft_edit_open"
          data-outcome="open"
          data-record-value="source"
          value="drafts_row"
        >
          <span className={styles.rowTop}>
            <span className={styles.from}>To: {draft.to || '(no recipient)'}</span>
          </span>
          <span className={styles.subject}>{draft.subject || '(no subject)'}</span>
          <span className={styles.snippet}>{draft.snippet}</span>
        </button>
        <div className={styles.draftRowActions}>
          <button
            type="button"
            className={styles.rowActionBtn}
            onClick={() => openEditDraft(draft)}
            data-action="tsc_draft_edit_open"
            data-outcome="open"
            data-record-value="source"
            value="drafts_row_edit"
          >
            ✎ Edit
          </button>
          <button
            type="button"
            className={styles.rowDangerBtn}
            disabled={acting === draft.draft_id}
            onClick={() => void onDiscardDraft(draft)}
            data-action="tsc_draft_discard"
            data-outcome="request"
            data-record-value="source"
            value="drafts_row"
          >
            🗑 Discard
          </button>
        </div>
      </div>
    </li>
  );

  const renderThreadRow = (row: MailThreadSummary) => {
    // Show the real sender address after the display name — a friendly name
    // alone can mask a spoofed sender (phishing triage).
    const senderAddr =
      folder === 'sent' ? null : senderAddressSuffix(row.from_name, row.from_address);
    return (
    <li key={row.thread_id}>
      <button
        type="button"
        className={`${styles.row} ${row.unread ? styles.rowUnread : ''}`}
        onClick={() => void openThread(row)}
        data-testid="mail-thread-row"
        data-action="tsc_thread_open"
        data-target-thread-id={row.thread_id}
        data-unread={row.unread ? 'true' : 'false'}
        aria-label={`${row.unread ? 'Unread: ' : ''}${row.subject || '(no subject)'}`}
      >
        <span className={styles.rowTop}>
          <span
            className={`${styles.from} ${row.unread ? styles.unreadName : ''}`}
            title={senderAddr ? `${row.from_name} <${senderAddr}>` : undefined}
          >
            {row.unread && (
              <span className={styles.unreadDot} title="Unread" aria-hidden="true" />
            )}
            {folder === 'sent'
              ? `To: ${row.to_address || row.from_name || '(unknown)'}`
              : row.from_name || row.from_address || 'Unknown sender'}
            {senderAddr && (
              <span className={styles.fromAddr}>{`<${senderAddr}>`}</span>
            )}
          </span>
          <span className={styles.time}>{shortDate(row.received_at)}</span>
        </span>
        <span className={`${styles.subject} ${row.unread ? styles.unreadSubject : ''}`}>
          {row.subject || '(no subject)'}
        </span>
        <span className={styles.snippet}>{row.snippet}</span>
        {folder === 'inbox' && (row.replied || draftFor(row.thread_id)) && (
          <span className={styles.rowBadges}>
            {row.replied && (
              <span
                className={styles.repliedBadge}
                title="You replied to this conversation — the reply is in Sent"
              >
                ↩ Replied
              </span>
            )}
            {draftFor(row.thread_id) && (
              <span className={styles.draftBadge}>✎ draft ready</span>
            )}
          </span>
        )}
      </button>
    </li>
    );
  };

  // Server count when it reported one; otherwise the loaded inbox rows.
  const inboxUnread =
    unreadCount ?? (folder === 'inbox' ? threads.filter((t) => t.unread).length : 0);

  const emptyLabel: Record<PanelFolder, string> = {
    inbox: 'No conversations',
    drafts: 'No drafts',
    sent: 'Nothing sent yet',
    trash: 'Trash is empty',
  };

  return (
    <section className={styles.panel} aria-label="Tech Support inbox">
      <header className={styles.head}>
        {composer ? (
          <>
            <button
              type="button"
              className={styles.back}
              onClick={closeComposer}
              data-action="tsc_composer_back"
              data-outcome="close"
            >
              ← Back
            </button>
            <button
              type="button"
              className={styles.templatesBtn}
              onClick={openTemplatesInComposer}
              disabled={composerBusy}
              title="Insert one of John's house-standard replies at the cursor"
              data-action="tsc_templates_open"
              data-outcome="open"
              data-record-value="source"
              value="composer_header"
            >
              📋 Templates
            </button>
          </>
        ) : selectedId ? (
          <button
            type="button"
            className={styles.back}
            onClick={back}
            data-action="tsc_thread_back"
            data-outcome="close"
            data-target-thread-id={selectedId}
            data-record-value="folder"
            value={folder}
          >
            ← {FOLDERS.find((f) => f.key === folder)?.label ?? 'Inbox'}
          </button>
        ) : (
          <div className={styles.headTitle}>
            <span className={styles.title}>
              {FOLDERS.find((f) => f.key === folder)?.label ?? 'Inbox'}
            </span>
            {accountEmail && <span className={styles.account}>{accountEmail}</span>}
          </div>
        )}
        {!selectedId && !composer && (
          <div className={styles.headBtns}>
            <button
              type="button"
              className={styles.composeBtn}
              onClick={openCompose}
              title="Compose a new email"
              data-action="tsc_compose_open"
              data-outcome="open"
            >
              ✎ Compose
            </button>
            <button
              type="button"
              className={styles.refresh}
              onClick={() => void loadThreads()}
              disabled={loadingList}
              aria-label="Refresh inbox"
              title="Refresh"
              data-action="tsc_inbox_refresh"
              data-outcome="request"
            >
              ↻
            </button>
          </div>
        )}
      </header>

      {!selectedId && !composer && !notConnected && (
        <>
          <nav className={styles.folders} aria-label="Mailbox folders">
            {FOLDERS.map((f) => (
              <button
                key={f.key}
                type="button"
                className={`${styles.folderTab} ${folder === f.key ? styles.folderTabOn : ''}`}
                aria-selected={folder === f.key}
                data-action="tsc_folder_tab"
                data-record-value="folder"
                value={f.key}
                onClick={() => switchFolder(f.key)}
              >
                {f.label}
                {f.key === 'inbox' && inboxUnread > 0 && (
                  <span
                    className={`${styles.folderCount} ${styles.unreadCount}`}
                    title={`${inboxUnread} unread`}
                    data-testid="inbox-unread-count"
                  >
                    {inboxUnread}
                  </span>
                )}
                {f.key === 'drafts' && drafts.length > 0 && (
                  <span className={styles.folderCount}>{drafts.length}</span>
                )}
              </button>
            ))}
          </nav>
          {folder !== 'drafts' && (
            <div className={styles.searchRow}>
              <input
                className={styles.searchInput}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submitSearch();
                }}
                placeholder="Search all mail — try from:acme.com, subject:…, or an exact phrase in quotes"
                aria-label="Search mail"
              />
              {activeSearch ? (
                <button
                  type="button"
                  className={styles.searchClear}
                  onClick={() => {
                    setSearchInput('');
                    setActiveSearch('');
                  }}
                  title="Clear search"
                  data-action="tsc_search_clear"
                  data-outcome="request"
                >
                  ✕
                </button>
              ) : (
                <button
                  type="button"
                  className={styles.searchGo}
                  onClick={submitSearch}
                  title="Search"
                  aria-label="Search"
                  data-action="tsc_search_go"
                  data-outcome="request"
                >
                  🔍
                </button>
              )}
            </div>
          )}
        </>
      )}

      <div className={styles.body} ref={bodyRef} data-testid="mail-panel-body">
        {sendNote && !composer && (
          <div className={styles.sendBanner} role="status">
            {sendNote}
          </div>
        )}
        {composer ? (
          <>
            <TechSupportComposer
              init={composer}
              accountEmail={accountEmail}
              signatureHtml={signatureHtml}
              onEditSignature={onEditSignature}
              onCancel={closeComposer}
              onSaved={onComposerSaved}
              onSent={onComposerSent}
              openTemplatesSignal={templatesSignal}
              onBusyChange={setComposerBusy}
            />
            {/* The email being answered stays right below the editor — John can
                scroll it while he writes instead of losing it to the composer. */}
            {(composerThreadLoading ||
              (composerThread && composerThread.emails.length > 0)) && (
              <div className={styles.composerContext}>
                <div className={styles.composerContextLabel}>
                  {composer.mode === 'forward'
                    ? 'Conversation being forwarded'
                    : 'Conversation you are replying to'}
                </div>
                {composerThreadLoading ? (
                  <div className={styles.muted}>Loading the original conversation…</div>
                ) : (
                  composerThread && (
                    <EmailChainThread
                      emails={composerThread.emails}
                      downloadAttachment={coPilotDownload}
                    />
                  )
                )}
              </div>
            )}
          </>
        ) : selectedId ? (
          <div className={styles.thread}>
            <div className={styles.threadHead} data-target-thread-id={selectedId}>
              <div className={styles.threadSubject}>{selectedSubject}</div>
              <button
                type="button"
                className={styles.templatesBtn}
                onClick={openTemplatesComposer}
                disabled={!draftFor(selectedId) && (loadingThread || !thread)}
                title="Open the editor with a house-standard reply template, inserted where your cursor is"
                data-action="tsc_templates_open"
                data-outcome="open"
                data-record-value="source"
                value="thread_head"
              >
                📋 Templates
              </button>
              <button
                type="button"
                className={styles.replyBtn}
                onClick={() => openReply()}
                disabled={loadingThread || !thread}
                title="Reply to this conversation (the original is quoted below your message)"
                data-action="tsc_reply_open"
                data-outcome="open"
                data-record-value="mode"
                value="reply"
              >
                ↩ Reply
              </button>
              <button
                type="button"
                className={styles.threadActionBtn}
                onClick={() => openReply(false, true)}
                disabled={loadingThread || !thread}
                title="Reply to everyone on the conversation"
                data-action="tsc_reply_open"
                data-outcome="open"
                data-record-value="mode"
                value="reply_all"
              >
                ↩ All
              </button>
              <button
                type="button"
                className={styles.threadActionBtn}
                onClick={openForward}
                disabled={loadingThread || !thread}
                title="Forward this conversation (its files are included)"
                data-action="tsc_reply_open"
                data-outcome="open"
                data-record-value="mode"
                value="forward"
              >
                ↪ Forward
              </button>
              {folder === 'inbox' && (
                <>
                  <button
                    type="button"
                    className={styles.threadActionBtn}
                    onClick={() => void onMarkUnread()}
                    disabled={!!acting}
                    title="Mark as unread — the dot comes back on this conversation in the inbox"
                    data-action="tsc_thread_mark_unread"
                    data-outcome="request"
                  >
                    ● Mark unread
                  </button>
                  <button
                    type="button"
                    className={styles.threadActionBtn}
                    onClick={() => void onArchive()}
                    disabled={!!acting}
                    title="Archive — out of the inbox, still searchable"
                    data-action="tsc_thread_archive"
                    data-outcome="request"
                  >
                    🗂 Archive
                  </button>
                </>
              )}
              {folder !== 'trash' && (
                <button
                  type="button"
                  className={styles.threadDangerBtn}
                  onClick={() => void onTrash()}
                  disabled={!!acting}
                  title="Move to Trash"
                  data-action="tsc_thread_trash"
                  data-outcome="request"
                >
                  🗑 Trash
                </button>
              )}
            </div>
            {draftFor(selectedId) && renderDraftCard(draftFor(selectedId)!)}
            {loadingThread && <div className={styles.muted}>Loading conversation…</div>}
            {threadError && <div className={styles.error}>{threadError}</div>}
            {thread && (
              <EmailChainThread
                emails={thread.emails}
                downloadAttachment={coPilotDownload}
              />
            )}
          </div>
        ) : notConnected ? (
          <div className={styles.empty}>
            <div className={styles.emptyIcon} aria-hidden="true">
              ✉️
            </div>
            <div className={styles.emptyTitle}>Inbox not connected yet</div>
            <p className={styles.emptyBody}>
              The Tech Support mailbox isn’t linked in this environment. Once it’s
              connected, your conversations show up right here.
            </p>
          </div>
        ) : listError ? (
          <div className={styles.empty}>
            <div className={styles.error}>{listError}</div>
            <button
              type="button"
              className={styles.retry}
              onClick={() => void loadThreads()}
              data-action="tsc_list_retry"
              data-outcome="request"
            >
              Try again
            </button>
          </div>
        ) : loadingList ? (
          <div className={styles.muted}>Loading inbox…</div>
        ) : folder === 'drafts' ? (
          drafts.length === 0 ? (
            <div className={styles.empty}>
              <div className={styles.emptyIcon} aria-hidden="true">
                📭
              </div>
              <div className={styles.emptyTitle}>{emptyLabel.drafts}</div>
            </div>
          ) : (
            <ul className={styles.list}>{drafts.map(renderDraftRow)}</ul>
          )
        ) : threads.length === 0 ? (
          <div className={styles.empty}>
            <div className={styles.emptyIcon} aria-hidden="true">
              📭
            </div>
            <div className={styles.emptyTitle}>
              {activeSearch ? 'No results for this search' : emptyLabel[folder]}
            </div>
          </div>
        ) : (
          <>
            <ul className={styles.list}>{threads.map(renderThreadRow)}</ul>
            {nextPageToken && (
              <button
                type="button"
                className={styles.loadMore}
                onClick={() => void loadMore()}
                disabled={loadingMore}
                data-action="tsc_list_load_more"
                data-outcome="request"
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            )}
          </>
        )}
      </div>
    </section>
  );
}
